import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/components/ui/Dialog';
import { Button } from '@/shared/components/ui/Button';
import { useCreateTask } from '@/shared/hooks';
import type { RelatedSignalsApiResponse } from '@momentum/types';
import { defaultValues, type CreateTaskFormData } from './types/tasks.types';
import { TaskForm } from './TaskForm';
import { useEmployeeContext } from '@/shared/contexts/EmployeeContext';

interface CreateTaskModalProps {
  parentGoalId?: string;
  trigger?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const CreateTaskModal: React.FC<CreateTaskModalProps> = ({
  parentGoalId,
  trigger,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}) => {
  const { t } = useTranslation('goals');
  const [internalOpen, setInternalOpen] = useState(false);
  const createTaskMutation = useCreateTask();
  const currentEmployee = useEmployeeContext();

  const initialValues = useMemo<CreateTaskFormData>(() => {
    return {
      ...defaultValues,
      assignees: [currentEmployee._id],
      ...(parentGoalId && { parent: parentGoalId }),
      viewers: {
        users: [currentEmployee._id],
        teams: [],
      },
    };
  }, [currentEmployee._id, parentGoalId]);

  // Use controlled or internal state
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = controlledOnOpenChange || setInternalOpen;

  const handleSubmit = async (
    formData: CreateTaskFormData,
    signals: RelatedSignalsApiResponse[],
    hasReceivedSignalSuggestions?: boolean
  ) => {
    const { description, ...taskData } = formData;
    await createTaskMutation.mutateAsync({
      task: {
        ...taskData,
        ...(description?.trim() ? { description } : {}),
        assignees: taskData.assignees ?? [],
        relatedSignals: signals.map((signal) => ({
          signalId: signal.id,
          addedAt: new Date().toISOString(),
          addedBy: currentEmployee._id,
          ...(signal.contextualTitle
            ? { contextualTitle: signal.contextualTitle }
            : {}),
        })),
      },
      hasReceivedSignalSuggestions,
    });

    setOpen(false);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const defaultTrigger = (
    <Button
      size="sm"
      className="shadow-sm hover:shadow-md transition-shadow rtl:flex-row-reverse"
    >
      <Plus className="h-4 w-4 me-2" />
      {t('createTaskButton')}
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      {!trigger && <DialogTrigger asChild>{defaultTrigger}</DialogTrigger>}
      <DialogContent
        size="lg"
        className="flex flex-col max-h-[90vh] p-0"
        dismissibleByOutsideClick={false}
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border shrink-0">
          <DialogTitle className="text-xl font-bold">
            {t('createTaskTitle')}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 min-h-0 px-6 py-4">
          <TaskForm
            initialValues={initialValues}
            isPending={createTaskMutation.isPending}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            submitLabel={t('createTask')}
            submittingLabel={t('creating')}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
